<template>
  <div
    class="d-flex flex-column  align-center ga-5 w100 pa-5 fill-height"
  >
    <div class="d-flex flex-column align-center ga-1">
      <div class="d-flex align-center ga-2">
        <v-icon style="font-size: 40px; color: #43BA7E;">
          mdi-qrcode
        </v-icon>
        <h1>{{ title }}</h1>
      </div>
      <span
        style="color: rgba(0,0,0,0.6); font-size: 20px; font-weight: 500;"
      >{{ secondTitle }}</span>
    </div>
    <div class="d-flex ga-6">
      <v-btn
        class="pt-8 pb-8 pl-16 pr-16"
        style="border-radius: 6px;"
        color="primary"
        :outlined="isTyping ? true : false"
        @click="isTyping = false"
      >
        <div class="d-flex align-center ga-2">
          <v-icon style="font-size: 26px;">
            mdi-camera
          </v-icon>
          <span style="font-size: 20px;"> Scan QR Code</span>
        </div>
      </v-btn>
      <v-btn
        class="pt-8 pb-8 pl-16 pr-16"
        style="border-radius: 6px;"
        color="primary"
        :outlined="isTyping ? false : true"
        @click="isTyping = true"
      >
        <div class="d-flex align-center ga-2">
          <v-icon style="font-size: 26px;">
            mdi-keyboard
          </v-icon>
          <span style="font-size: 20px;"> Type Code</span>
        </div>
      </v-btn>
    </div>
    <div
      style="border: 1px solid #E4E4E7; border-radius: 10px; min-width: 700px;"
      class="d-flex flex-column pa-4"
    >
      <h2>{{ isTyping ? 'Manual Code Entry' : 'QR Code Scanner' }}</h2>
      <span
        style="color: rgba(0,0,0,0.6); font-size: 16px; font-weight: 500;"
      >{{ isTyping ? 'Enter the QR/UPC/MPQR code manually' : 'Position the QR code within the frame to scan' }}</span>
      <div>
        <div
          v-if="isTyping"
          class="d-flex flex-column ga-2 pt-4"
        >
          <span>Code</span>
          <v-text-field
            v-if="isTyping"
            v-model="qrCode"
            outlined
            placeholder="Enter QR/UPC/MPQR code"
            :type="qrInputType"
            hide-details
            @keyup.enter="manualEntered()"
          />
          <v-btn
            class="pa-6"
            color="success"
            @click="manualEntered()"
            @keyup.enter="manualEntered()"
          >
            Submit Code
          </v-btn>
        </div>
        <div
          v-else
          style="min-height: 400px;"
          class="d-flex flex-column"
        >
          <qrcode-stream
            :camera="camera"
            :track="paintBoundingBox"
            @decode="processResult"
            @init="onInit"
          >
            <canvas
              id="square-canvas"
              class="overlay-line"
            />
          </qrcode-stream>
          <div class="d-flex align-center justify-center w100">
            <v-btn
              color="primary"
              class="pa-5"
              outlined
              style="font-size: 1.1em; margin-top: 75px"
              @click="changeCamera"
            >
              Switch Back/Front Camera
            </v-btn>
          </div>
        </div>
      </div>
    </div>
    <div
      style="background-color: #F0F6FF; border: 1px solid #C5DBFF; border-radius: 10px; min-width: 700px;"
      class="pa-4 d-flex flex-column ga-1"
    >
      <span
        style="color: #335FCD"
        class="fw500"
      >Instructions: </span>
      <div class="d-flex flex-column ga-1">
        <span style="color: #335FCD">- Ensure the QR code label is clean and undamaged</span>
        <span style="color: #335FCD">- Hold the device steady when scanning</span>
        <span style="color: #335FCD">- For manual entry, double-check the code accuracy</span>
        <span style="color: #335FCD">- Attach the label to the receiving bundle after scanning</span>
      </div>
    </div>
    <div v-show="false">
      <inventory-qr-form
        ref="tagForm"
        @success="submitQRCodeTag"
      />
      <inventory-upc-form
        ref="upcForm"
        @success="submitQRCodeTag"
      />
      <inventory-upc-new-form
        ref="newUpcForm"
        :entered-tag="qrCode"
        @submitQRCodeTag="submitQRCodeTag"
      />
      <inventory-qr-new-form
        ref="newQrForm"
        :entered-tag="qrCode"
        :is-show-p-o="true"
        :is-show-location="true"
        @submitQRCodeTag="submitQRCodeTag"
      />
      <InventoryMPQRForm
        ref="mpqrForm"
        @success="submitQRCodeTag"
      />
      <glass-batch-qr-form
        ref="batchForm"
      />
      <glass-move-qr-form
        ref="glassMoveQr"
      />
      <window-qr-form
        ref="windowQr"
      />
      <bill-of-lading-form
        ref="billOfLadingForm"
      />
      <commercial-schedule-qr-form
        ref="commercialScheduleQrForm"
      />
      <commercial-bill-of-lading-form
        ref="commercialBillOfLadingForm"
      />
      <wip-panel-batch-qr-form
        ref="wipPanelBatchQrForm"
      />
      <screen-bill-of-lading-form
        ref="screenBillOfLadingQr"
      />
      <screen-qr-form
        ref="screenQr"
      />
      <commercial-bundle-form
        ref="commercialBundleForm"
      />
      <commercial-skid-form
        ref="commercialSkidForm"
      />
    </div>
  </div>
</template>

<script>
import { QrcodeStream } from 'vue-qrcode-reader';
import { mapGetters, mapState } from 'vuex';
import InventoryQrForm from '@/components/forms/inventoryQrs/InventoryQrForm.vue';
import InventoryQrNewForm from '@/components/forms/inventoryQrs/InventoryQrNewForm.vue';
import InventoryUpcForm from '@/components/forms/inventoryQrs/InventoryUpcForm.vue';
import InventoryUpcNewForm from '@/components/forms/inventoryQrs/InventoryUpcNewForm.vue';
import InventoryMPQRForm from '@/components/forms/inventoryQrs/InventoryMPQRForm';
import GlassBatchQrForm from '@/components/forms/glasses/GlassBatchQrForm.vue';
import GlassMoveQrForm from '@/components/forms/glasses/GlassMoveQrForm.vue';
import WindowQrForm from '@/components/forms/windowQrs/WindowQrForm.vue';
import BillOfLadingForm from '@/components/forms/billOfLadings/BillOfLadingForm.vue';
import CommercialScheduleQrForm from '@/components/forms/commercialSchedules/CommercialScheduleQrForm.vue';
import CommercialBillOfLadingForm from '@/components/forms/commercialBillOfLadings/CommercialBillOfLadingForm.vue';
import WipPanelBatchQrForm from '@/components/forms/wipPanels/WipPanelBatchQrForm.vue';
import ScreenBillOfLadingForm from '@/components/forms/screenBillOfLadings/ScreenBillOfLadingForm.vue';
import ScreenQrForm from '@/components/forms/screens/ScreenQrForm.vue';
import CommercialBundleForm from '@/components/forms/commercialBundles/CommercialBundleForm.vue';
import CommercialSkidForm from '@/components/forms/commercialSkids/CommercialSkidForm.vue';

export default {
  components: {
    QrcodeStream, InventoryQrForm, InventoryUpcForm, InventoryQrNewForm, InventoryUpcNewForm, InventoryMPQRForm, GlassBatchQrForm, GlassMoveQrForm, WindowQrForm, BillOfLadingForm, WipPanelBatchQrForm, ScreenBillOfLadingForm, ScreenQrForm, CommercialScheduleQrForm, CommercialBillOfLadingForm, CommercialBundleForm, CommercialSkidForm,
  },
  props: {
    msg: {
      type: String,
      default: '',
    },
    qrType: {
      type: String,
      default: '',
    },
    isRmSupply: Boolean,
    isPaintSupply: Boolean,
    isMovingQrPartially: Boolean,
    isPartPickList: Boolean,
  },
  data: () => ({
    loading: false,
    camera: 'rear',
    cameraState: '',
    error: '',
    openDialog: false,
    isQrEqualsTag: false,
    isTyping: false,
    qrCode: '',
    inventoryTag: '',
    isInventoryQr: false,
    isPhysicalCountQr: false,
    isRmReceivingQr: false,
    isMovingQr: false,
    isBatchQr: false,
    isScreenBillOfLadingQr: false,
    isStlQr: false,
    isGlassMovingQr: false,
    isWindowQr: false,
    isCommercialBundleQr: false,
    isCommercialSkidQr: false,
    isScreenQr: false,
    isBillOfLadingQr: false,
    isCommercialScheduleQr: false,
    isCommercialBillOfLadingQr: false,
    isWipPanelBatchQr: false,
    textfieldTitle: 'QR/UPC/MPQR Code',
    secondTitle: 'Please place QR code into the green square',
    title: 'Scan QR',
    data: null,
    inventoryClassIdParts: 11,
    inventoryClassIdInst: 4,
    inventoryClassIdPaint: 12,

  }),
  computed: {
    ...mapGetters([
      'businessRulesGetBoolean',
      'isPhysicalCountActivated',
      'currentPhysicalCount',
    ]),
    ...mapState({
      isQRCodeScan: 'isQRCodeScan',
      isDarkMode: 'isDarkMode',
    }),
    validationPending() {
      return this.camera === 'off';
    },
    qrInputType() {
      const numberTypes = ['isInventoryQr', 'isPhysicalCountQr', 'isRmReceivingQr', 'isMovingQr'];
      const textTypes = ['isBillOfLadingQr', 'isBatchQr'];

      if (numberTypes.some((type) => this[type])) {
        return 'number';
      } if (textTypes.some((type) => this[type])) {
        return 'text';
      }
      return 'text';
    },
    qrCodeIsMpqr() {
      return parseInt(this.qrCode, 10) > 199999 && parseInt(this.qrCode, 10) < 209999;
    },
    qrCodeIsUpc() {
      return parseInt(this.qrCode, 10) > 99999 && parseInt(this.qrCode, 10) < 199999;
    },
  },
  watch: {
    qrType: {
      immediate: true,
      handler(value) {
        if (!value) return;

        this.isInventoryQr = false;
        this.isPhysicalCountQr = false;
        this.isRmReceivingQr = false;
        this.isMovingQr = false;
        this.isBillOfLadingQr = false;
        this.isWipPanelBatchQr = false;
        this.isScreenBillOfLadingQr = false;
        this.isCommercialBundleQr = false;
        this.qrCode = '';
        switch (value) {
          case 'inventory-qr':
            this.isInventoryQr = true;
            break;
          case 'physical-count-qr':
            this.isPhysicalCountQr = true;
            break;
          case 'rm-receiving-qr':
            this.isRmReceivingQr = true;
            break;
          case 'moving-qr':
            this.isMovingQr = true;
            break;
          case 'batch-qr':
            this.isBatchQr = true;
            break;
          case 'glass-move-qr':
            this.isGlassMovingQr = true;
            break;
          case 'window-qr':
            this.isWindowQr = true;
            break;
          case 'bill-of-lading-qr':
            this.isBillOfLadingQr = true;
            break;
          case 'screen-bill-of-lading-qr':
            this.isScreenBillOfLadingQr = true;
            break;
          case 'stl-qr':
            this.isStlQr = true;
            break;
          case 'commercial-schedule-qr':
            this.isCommercialScheduleQr = true;
            break;
          case 'commercial-bill-of-lading-qr':
            this.isCommercialBillOfLadingQr = true;
            break;
          case 'wip-panel-batch-qr':
            this.isWipPanelBatchQr = true;
            break;
          case 'screen-qr':
            this.isScreenQr = true;
            break;
          case 'commercial-bundle-qr':
            this.isCommercialBundleQr = true;
            break;
          case 'commercial-skid-qr':
            this.isCommercialSkidQr = true;
            break;
          default:
        }
        this.setTitle();
      },
    },

    isTyping(val) {
      if (val) this.turnCameraOff();
      else this.turnCameraOn();

      if (!val) {
        this.$nextTick(async () => {
          this.manualBoundingBox();
        });
      }
      this.setTitle();
      this.$store.commit('storeIsQRCodeScan', val);
    },

    error(message) {
      this.$bus.$emit('showError', message);
    },
  },
  created() {
    this.isTyping = this.isQRCodeScan;
    this.initialize();
  },
  methods: {
    async initialize() {
      await this.$nextTick();
      this.turnCameraOn();

      this.manualBoundingBox();

      const QrEqualsTagBRName = 'QR Code Equals Inv. Tag';
      this.isQrEqualsTag = this.businessRulesGetBoolean(QrEqualsTagBRName);
    },

    async onInit(promise) {
      this.loading = true;
      try {
        await promise;
      } catch (error) {
        if (error.name === 'NotAllowedError') {
          this.error = 'ERROR: you need to grant camera access permisson';
        } else if (error.name === 'NotFoundError') {
          this.error = 'ERROR: no camera on this device';
        } else if (error.name === 'NotSupportedError') {
          this.error = 'ERROR: secure context required (HTTPS, localhost)';
        } else if (error.name === 'NotReadableError') {
          this.error = 'ERROR: is the camera already in use?';
        } else if (error.name === 'OverconstrainedError') {
          this.error = 'ERROR: installed cameras are not suitable';
        } else if (error.name === 'StreamApiNotSupportedError') {
          this.error = 'ERROR: Stream API is not supported in this browser';
        }
      } finally {
        this.loading = false;
      }
    },

    turnCameraOn() {
      this.camera = 'rear';
    },

    turnCameraOff() {
      this.cameraState = this.camera;
      this.camera = 'off';
    },

    changeCamera() {
      this.camera = this.camera === 'front' ? 'rear' : 'front';
    },

    manualBoundingBox() {
      const boxSizes = [];
      boxSizes.push({
        boundingBox: {
          x: 50.5, y: 10.5, width: 200, height: 130,
        },
      });
      const canvas = document.getElementById('square-canvas');
      const ctx = canvas.getContext('2d');
      this.paintBoundingBox(boxSizes, ctx);
    },

    paintBoundingBox(detectedCodes, ctx) {
      // eslint-disable-next-line no-restricted-syntax
      for (const detectedCode of detectedCodes) {
        const {
          boundingBox: {
            x, y, width, height,
          },
        } = detectedCode;

        ctx.lineWidth = 1;
        ctx.strokeStyle = '#5cb984';
        ctx.strokeRect(x, y, width, height);
      }
    },

    async processResult(result) {
      this.turnCameraOff();
      const resultArray = result.split('/');
      const qrCodeNumberIdx = 4;
      this.qrCode = resultArray[qrCodeNumberIdx];
      this.getQrCode();
    },

    async getQrCode() {
      if (!this.qrCode || this.qrCode === null) {
        this.$bus.$emit('showError', 'Unrecognized QR Code');
        return;
      }

      // Inventory Qr
      if (this.isInventoryQr && (parseInt(this.qrCode, 10) > 199999 && parseInt(this.qrCode, 10) < 209999)) { this.$router.push(`/inventory-mpqr/${this.qrCode}`); return; }
      if (this.isInventoryQr) this.$router.push(`/inventory-qr/${this.qrCode}`);

      // Physical Count
      if (this.isPhysicalCountQr && this.isPhysicalCountActivated) this.$router.push(`/physical-count-qr/${this.qrCode}`);

      // RM Receiving
      if (this.isRmReceivingQr) {
        const submitModel = {
          filters: {
            inventory_tag: this.qrCode,
            is_qty_on_hand_o: 0,
            is_user_location: false,
          },
        };
        const res = await this.$api.inventoryTag.read(submitModel);
        if (res && res.status === 505) {
          this.$bus.$emit('showError', res.message);
          this.isActivatedVisible = true;
          return;
        }

        // UPCs QR Codes: from 100000 to 199999
        if (parseInt(this.qrCode, 10) > 99999 && parseInt(this.qrCode, 10) < 199999) {
          if (this.data && this.data.parameter_setting && this.data.parameter_setting.is_upc !== 1) {
            this.$bus.$emit('showError', 'Item Class is not used for UPC codes');
            return;
          }
          if (!res || !res.data || !res.data[0]) {
            this.$refs.newUpcForm.openDialog(this.data);
          } else {
            if (this.data.inventory_item_id !== res.data[0].inventory_item_id || this.data.uom_id !== res.data[0].uom_id || this.data.length_id !== res.data[0].length_id || this.data.color_id !== res.data[0].color_id) {
              this.$bus.$emit('showError', 'Item Class, Item, Color, Length or UOM does not match with the QR Code.');
              return;
            }
            this.$refs.upcForm.open(this.qrCode, this.data);
            return;
          }
          // MPQR Codes: from 100000 to 199999
        } else if (parseInt(this.qrCode, 10) > 199999 && parseInt(this.qrCode, 10) < 209999) {
          if (this.data && this.data.inventory_class_id !== this.inventoryClassIdPaint
          ) {
            this.$bus.$emit('showError', "Item Class must be 'PAINT' to use this QR code.");
            return;
          }
          this.$refs.mpqrForm.open(this.qrCode, null, this.data);
        } else if (this.data.inventory_class_id === this.inventoryClassIdParts || this.data.inventory_class_id === this.inventoryClassIdInst || this.data.inventory_class_id === this.inventoryClassIdPaint) {
          this.$bus.$emit('showError', "Item Class can't be 'PAINT' or 'PARTS' or 'INST' to use this QR code.");
          return;
        } else if (!res || !res.data || !res.data[0]) {
          this.$refs.newQrForm.openDialog(this.data);
        } else {
          if (this.data.inventory_item_id !== res.data[0].inventory_item_id || this.data.uom_id !== res.data[0].uom_id || this.data.length_id !== res.data[0].length_id || this.data.color_id !== res.data[0].color_id) {
            this.$bus.$emit('showError', 'Item Class, Item, Color, Length or UOM does not match with the QR Code.');
            return;
          }
          this.$refs.tagForm.open(this.qrCode, this.data);
          return;
        }
      }

      // Moving QR
      if (this.isMovingQr) {
        if ((parseInt(this.qrCode, 10) > 199999 && parseInt(this.qrCode, 10) < 209999)) {
          this.$router.push(`/moving-qr/mpqr/${this.qrCode}`);
          return;
        }
        this.$router.push(`/moving-qr/${this.qrCode}`);
      }

      // return to Supply to Line, Supply to Paint, Part Pick List
      if (this.isRmSupply || this.isPaintSupply || this.isPartPickList) {
        const { qrCode } = this;
        this.$emit('return-qr-code', qrCode);
        this.$nextTick(async () => {
          this.qrCode = '';
        });
      }

      // return to Moving Qr if moving partially
      if (this.isMovingQrPartially) {
        const { qrCode } = this;
        this.$emit('return-qr-code-move', qrCode);
        this.$nextTick(async () => {
          this.qrCode = '';
        });
      }

      if (this.isBatchQr) {
        this.$refs.batchForm.open(this.qrCode);
      }

      if (this.isGlassMovingQr) { this.$refs.glassMoveQr.open(this.qrCode); }
      if (this.isBillOfLadingQr) {
        this.$refs.billOfLadingForm.open(this.qrCode, false, this.isBillOfLadingQr);
      }

      if (this.isScreenBillOfLadingQr) {
        this.$refs.screenBillOfLadingQr.open(this.qrCode, false, this.isScreenBillOfLadingQr);
      }

      if (this.isWindowQr) {
        this.$refs.windowQr.open(this.qrCode);
      }
      if (this.isScreenQr) {
        this.$refs.screenQr.open(this.qrCode);
      }
      if (this.isStlQr) {
        if (this.isPhysicalCountActivated) {
          // first we need to check if the qrCode exists in InventoryTags
          const submitModel = {
            filters: {
              inventory_tag: this.qrCode,
              is_qty_on_hand_o: 0,
            },
          };
          const res = await this.$api.inventoryTag.read(submitModel);
          if (res && res.status === 505) {
            this.$bus.$emit('showError', res.message);
            this.isActivatedVisible = true;
            return;
          }
          if (res && res.data && res.data[0]) {
            if (this.qrCodeIsMpqr) {
              // qr code is MPQR and exists, then go to MPQR route
              this.$router.push(`/stl-qr/${this.qrCode}`);
              return;
            }
            // qr code exists in InventoryTags
            const inventoryTag = res.data[0];
            const inventoryClassId = inventoryTag.item.inventory_class_id;
            // now we need to check if the class of the qr code is part of the physical count
            console.log('currentPhysicalCount', this.currentPhysicalCount);
            if (this.currentPhysicalCount && this.currentPhysicalCount.classes && this.currentPhysicalCount.classes.some((c) => c.id === inventoryClassId)) {
              // class is part of the physical count so now we need to check if there already exists a physical count record for this qrCode
              const submitModel2 = {
                filters: {
                  inventory_tag: this.qrCode,
                  physical_count_list_id: this.currentPhysicalCount.id,
                },
              };
              const res2 = await this.$api.physicalCount.read(submitModel2);
              if (res2 && res2.status === 505) {
                this.$bus.$emit('showError', res2.message);
                this.isActivatedVisible = true;
                return;
              }
              if (res2 && res2.data && res2.data[0]) {
                // physical count record exists for this qrCode so we open STL QR Form
                this.$router.push(`/stl-qr/${this.qrCode}`);
                return;
              }
              // physical count record does not exist for this qrCode so we need to tell the user to create it
              this.$bus.$emit('showError', 'Please update the quantity of the QR Code first, using Physical Count QR');
              return;
            }
            // class is NOT part of the physical count so we just open STL QR Form
            this.$router.push(`/stl-qr/${this.qrCode}`);
            return;
          }
          // qr code does not exist
          this.$bus.$emit('showError', 'Code not found');
          return;
        }
        this.$router.push(`/stl-qr/${this.qrCode}`);
      }

      if (this.isBillOfLadingQr) {
        const submitModel = {
          filters: {
            code: this.qrCode,
          },
        };
        const res = await this.$api.billOfLading.read(submitModel);
        const cancelCode = 3001;
        if (res === cancelCode) return;
        if (!res || !res.data || res.data.length === 0) {
          this.$bus.$emit('showError', 'Bill of lading not found');
          return;
        }
        this.$refs.billOfLadingForm.open(this.qrCode, false, this.isBillOfLadingQr);
      }


      if (this.isCommercialScheduleQr) {
        this.$refs.commercialScheduleQrForm.open(this.qrCode);
      }

      if (this.isCommercialBillOfLadingQr) {
        this.$refs.commercialBillOfLadingForm.open(this.qrCode, false, this.isCommercialBillOfLadingQr);
      }

      if (this.isWipPanelBatchQr) {
        this.$refs.wipPanelBatchQrForm.open(this.qrCode);
      }

      if (this.isCommercialBundleQr) {
        const isViewing = true;
        const isCommercialBundleQr = true;
        this.$refs.commercialBundleForm.open(null, isViewing, this.qrCode, isCommercialBundleQr);
      }

      if (this.isCommercialSkidQr) {
        const isViewing = true;
        const isCommercialSkidQr = true;
        this.$refs.commercialSkidForm.open(null, isViewing, this.qrCode, isCommercialSkidQr);
      }
    },

    manualEntered() {
      this.getQrCode();
    },

    async submitQRCodeTag(msg) {
      if (!this.isRmReceivingQr) {
        return;
      }

      this.$bus.$emit('showSuccess', msg);
      this.$emit('success');

      if (this.isTyping) this.qrCode = '';

      else this.turnCameraOn();
    },

    async setTitle() {
      if (this.isMovingQr || this.isRmSupply || this.isPaintSupply || this.isPartPickList || this.isMovingQrPartially) {
        this.title = this.isTyping ? 'Type QR/UPC' : 'Scan QR/UPC';
        this.secondTitle = this.isTyping ? 'Please type QR/UPC Code into the field' : 'Please place QR/UPC code into the green square';
        return;
      }
      if (this.isBatchQr) {
        this.title = 'Type Batch QR Code';
        this.secondTitle = 'Please type Batch QR Code into the field';
        this.textfieldTitle = 'Batch QR Code';
        return;
      }
      if (this.isWipPanelBatchQr) {
        this.title = 'Type WIP Panel Batch QR Code';
        this.secondTitle = 'Please type WIP Panel Batch QR Code into the field';
        this.textfieldTitle = 'WIP Panel Batch QR Code';
        return;
      }
      if (this.isGlassMovingQr) {
        this.title = 'Glass Reject / Rework QR';
        this.secondTitle = 'Please place QR code into the green square';
        this.textfieldTitle = 'Glass QR Code';
        return;
      }
      if (this.isWindowQr) {
        this.title = 'Production QR';
        this.secondTitle = 'Please place QR code into the green square';
        this.textfieldTitle = 'Production QR Code';
        return;
      }
      if (this.isScreenQr) {
        this.title = 'Screen QR';
        this.secondTitle = 'Please place QR code into the green square';
        this.textfieldTitle = 'Screen QR Code';
        return;
      }
      if (this.isStlQr) {
        this.title = 'Supply to Line QR';
        this.secondTitle = 'Please place QR code into the green square';
        return;
      }
      if (this.isBillOfLadingQr) {
        this.title = 'Type Bill Of Lading QR Code';
        this.secondTitle = 'Please type Bill Of Lading QR Code into the field';
        this.textfieldTitle = 'Bill Of Lading QR Code';
        return;
      }
      if (this.isScreenBillOfLadingQr) {
        this.title = 'Type Screen Bill Of Lading QR Code';
        this.secondTitle = 'Please type Screen Bill Of Lading QR Code into the field';
        this.textfieldTitle = 'Screen Bill Of Lading QR Code';
        return;
      }
      if (this.isCommercialScheduleQr) {
        this.title = 'Curtainwall Schedule QR';
        this.secondTitle = 'Please place QR code into the green square';
        this.textfieldTitle = 'Curtainwall Schedule QR Code';
        return;
      }
      if (this.isCommercialBillOfLadingQr) {
        this.title = 'Curtainwall Bill Of Lading QR';
        this.secondTitle = 'Please place QR code into the green square';
        this.textfieldTitle = 'Curtainwall Bill Of Lading QR Code';
        return;
      }
      switch (this.qrType) {
        case 'inventory-qr':
          this.title = 'Inventory QR';
          if (this.isTyping) { this.secondTitle = 'Please type QR Code number of Inventory Item'; } else this.secondTitle = 'Please scan QR Code of Inventory Item';
          break;
        case 'physical-count-qr':
          await this.getActivePhysicalCountList();
          this.title = `Physical Count (${this.physicalCountList ? this.physicalCountList.name : ''}, as of ${new Date(`${this.physicalCountList ? this.physicalCountList.date : ''} 00:00`).toDateString().substring(4)})`;
          if (this.isTyping) { this.secondTitle = 'Please type QR Code number of Inventory Item that needs to be counted'; } else this.secondTitle = 'Please scan QR Code of Inventory Item that needs to be counted';
          break;
        case 'rm-receiving-qr':
          this.title = 'Raw Materials Receiving';
          if (this.isTyping) { this.secondTitle = 'Please attach the unused QR Code label to Receiving bundle and type its number'; } else this.secondTitle = 'Please attach the unused QR Code label to Receiving bundle and scan it';
          break;
        case 'moving-qr':
          this.title = 'Inventory Item Move between locations/shelfs';
          if (this.isTyping) { this.secondTitle = 'Please type QR Code number of Inventory Item that needs to be moved'; } else this.secondTitle = 'Please scan QR Code of Inventory Item that needs to be moved';
          break;
        default:
          this.title = '';
          this.secondTitle = '';
          break;
      }
    },

    async getActivePhysicalCountList() {
      this.physicalCountList = await this.$api.physicalCountList.getActive();
    },

    setData(data) {
      this.data = data;
    },
  },
};
</script>
<style scoped>
.overlay-line {
  position: absolute;
  width: 100%;
  height: 100%;
  min-height: 400px;
  text-align: center;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
}
.v-text-field--outlined .v-input__prepend-outer, .v-text-field--outlined .v-input__append-outer {
    margin-top: 0px;
}
.v-application--is-ltr .v-input__append-outer {
    margin-left: 1px;
}

</style>
